import { useCallback, useEffect, useState } from "react";
import { Mail, Check, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import TabHeader from "@/components/admin/TabHeader";

export function useInboxCount(enabled) {
  const [count, setCount] = useState(0);
  const refresh = useCallback(() => {
    if (enabled) api.get("/admin/inbox/count").then((r) => setCount(r.data.new)).catch(() => {});
  }, [enabled]);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh]);
  return [count, refresh];
}

const VIEWS = [["new", "New"], ["done", "Done"], ["archived", "Archived"]];
const ACTIONS = {
  new: [["done", "Mark done", Check], ["archived", "Archive", Archive]],
  done: [["new", "Move to new", RotateCcw], ["archived", "Archive", Archive]],
  archived: [["new", "Move to new", RotateCcw]],
};

function Message({ m, view, onMove }) {
  const subject = `Re: ${m.projectType || `your ${m.section} inquiry`}`;
  return (
    <div data-testid={`inbox-msg-${m.id}`} className={`bg-paper p-6 ${view === "new" ? "border-l-2 border-clay" : ""}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-2xl font-extralight text-ink">{m.name}</p>
          <p className="font-body text-xs text-muted">{m.email}{m.company ? ` · ${m.company}` : ""} · {new Date(m.created_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <span className="border border-ink/15 px-2.5 py-1 font-mono text-[0.55rem] uppercase tracking-widest text-stone">{m.section}</span>
          {m.projectType && <span className="border border-clay/30 px-2.5 py-1 font-mono text-[0.55rem] uppercase tracking-widest text-clay">{m.projectType}</span>}
        </div>
      </div>
      <p data-testid={`inbox-msg-body-${m.id}`} className="mt-4 whitespace-pre-line font-body text-sm leading-relaxed text-ink">{m.message}</p>
      <div className="mt-5 flex flex-wrap items-center gap-4 border-t border-hairline pt-4">
        <a href={`mailto:${m.email}?subject=${encodeURIComponent(subject)}`} data-testid={`inbox-reply-${m.id}`} className="inline-flex items-center gap-2 bg-ink px-4 py-2 text-paper">
          <Mail className="h-3.5 w-3.5" /><span className="overline text-[0.6rem]">Reply</span>
        </a>
        {ACTIONS[view].map(([to, label, Icon]) => (
          <button key={to} type="button" data-testid={`inbox-${to}-${m.id}`} onClick={() => onMove(m, to)} className="inline-flex items-center gap-1.5 font-body text-xs text-stone hover:text-ink">
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
        {m.handled_by && view !== "new" && <span className="ml-auto font-body text-[0.7rem] text-muted">by {m.handled_by}</span>}
      </div>
    </div>
  );
}

export default function InboxTab({ onCountChange }) {
  const [view, setView] = useState("new");
  const [msgs, setMsgs] = useState(null);
  const load = (v = view) => api.get("/admin/inbox", { params: { status: v } }).then((r) => setMsgs(r.data));
  useEffect(() => { setMsgs(null); load(view); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = async (m, status) => {
    try {
      await api.patch(`/admin/inbox/${m.id}`, { status });
      setMsgs((cur) => cur.filter((x) => x.id !== m.id));
      onCountChange?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-inbox-tab">
      <TabHeader title="Inbox" note="Every message from the contact page, fittings and order requests. Mark them done once you’ve replied." />
      <div className="mb-6 flex gap-1">
        {VIEWS.map(([v, l]) => (
          <button key={v} type="button" data-testid={`inbox-view-${v}`} onClick={() => setView(v)}
            className={`px-4 py-2 font-body text-sm transition-colors ${view === v ? "bg-ink text-paper" : "bg-paper text-stone hover:text-ink"}`}>{l}</button>
        ))}
      </div>
      {msgs && msgs.length === 0 && <p data-testid="inbox-empty" className="font-display text-2xl font-extralight text-stone">{view === "new" ? "All caught up." : "Nothing here."}</p>}
      <div className="space-y-4">{msgs?.map((m) => <Message key={m.id} m={m} view={view} onMove={move} />)}</div>
    </div>
  );
}
